import type { LlmModelSelectDto } from '@@types/api.types';
import { useChat } from '@ai-sdk/react';
import { LlmUIMessage as LlmChatMessage } from '@azymmetric/shared/types/llm.types';
import { ConversationEmptyState } from '@components/ai-elements/conversation';
import { Modal } from '@components/core/modal.component';
import { ConfirmationModal } from '@components/shared/confirm-delete-modal.component';
import type { PromptGuidelines } from '@components/shared/prompt-popover.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import { useBuildHeaders } from '@hooks/api.hook';
import { errorToast } from '@hooks/toast.hook';
import { generateUUID } from '@lib/utils';
import { llmModelStore } from '@stores/llm-model.store';
import { DefaultChatTransport } from 'ai';
import { BookOpenIcon, GripVertical, SparklesIcon } from 'lucide-react';
import React, { type FC, type ReactNode } from 'react';
import { Group, Panel, Separator, useDefaultLayout } from 'react-resizable-panels';
import { useStore } from 'zustand';
import { LlmChatInput } from './llm-chat-input.component';
import { LlmChatMessages } from './llm-chat-messages.component';
import {
  type LlmChatComment,
  type LlmChatSuggestion,
  deriveLatestSuggestion,
  resolveSuggestionWithSeed,
} from './llm-chat.util';
import { SuggestionArtifactPanel } from './suggestion-artifact-panel.component';
import { useSuggestionResolutionLog } from './suggestion-resolution-log.hook';

const GENERATE_DRAFT_MESSAGE = 'Generate a draft for this field.';

export type LlmChatModalProps = {
  /**
   * Chat endpoint streaming UI messages with the proposeSuggestion tool
   * (e.g. /v1/expert/mission-assessments/chat).
   */
  apiEndpoint: string;
  /**
   * Request context sent with every exchange (e.g. fieldName, technologyId, effectContext).
   */
  data: Record<string, unknown>;
  description?: ReactNode;
  /**
   * Label for the empty-state one-click generate button (default: "Generate draft").
   */
  generateDraftLabel?: string;
  /**
   * Canonical first message the one-click generate action sends
   * (default: "Generate a draft for this field.").
   */
  generateDraftMessage?: string;
  information?: PromptGuidelines;
  /**
   * Prefill for the chat input when the modal opens (not auto-sent).
   */
  initialInput?: string;
  /**
   * Seed the suggestion panel with already-persisted content (UPDATE mode).
   * Shown until a chat draft supersedes it; Accept stays disabled while it is
   * the only version, and it is never accept/reject-logged.
   */
  initialSuggestionData?: Record<string, unknown>;
  isOpen: boolean;
  modelFilter?: (model: LlmModelSelectDto) => boolean;
  /**
   * Accept a plain-text Suggestion artifact into the form field.
   */
  onAccept?: (value: string) => void;
  /**
   * Accept a structured-object Suggestion artifact (server-validated and
   * resolved). Return false — or a promise resolving to false, or a rejection —
   * when the acceptance failed to persist: the modal stays open with the draft
   * intact and skips accept telemetry. void/true closes the modal.
   */
  onAcceptData?: (data: Record<string, unknown>, meta?: { logId?: string }) => Promise<boolean> | boolean | void;
  onClose: () => void;
  /**
   * Optional surface-specific renderer for structured-object drafts in the
   * artifact panel. Return null to fall back to the generic label/value view.
   */
  renderDataArtifact?: (data: Record<string, unknown>) => ReactNode;
  title?: string;
};

export const LlmChatModal: FC<LlmChatModalProps> = (props) => {
  const {
    apiEndpoint,
    data,
    description,
    generateDraftLabel,
    generateDraftMessage,
    information,
    initialInput,
    initialSuggestionData,
    isOpen,
    modelFilter,
    onAccept,
    onAcceptData,
    onClose,
    renderDataArtifact,
    title,
  } = props;

  const { buildHeaders } = useBuildHeaders();
  const { selectedModel } = useStore(llmModelStore);

  // useChat keeps the transport instance from the first render, so
  // prepareSendMessagesRequest must read the request-body inputs through a ref —
  // capturing them directly would freeze the selected model (and request data)
  // at mount time and silently ignore later changes.
  const requestBodyRef = React.useRef({ data, selectedModel });
  requestBodyRef.current = { data, selectedModel };

  const chatIdRef = React.useRef(generateUUID());
  const chatInputRef = React.useRef<HTMLTextAreaElement>(null);
  const [input, setInput] = React.useState('');
  const [isAccepting, setIsAccepting] = React.useState(false);
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = React.useState(false);
  const [pendingComments, setPendingComments] = React.useState<LlmChatComment[]>([]);
  const wasOpenRef = React.useRef(false);

  const panelLayout = useDefaultLayout({
    id: 'llm-chat-modal-layout',
    panelIds: ['chat', 'suggestion'],
  });

  const { messages, sendMessage, setMessages, status, stop } = useChat<LlmChatMessage>({
    id: chatIdRef.current,
    generateId: generateUUID,
    transport: new DefaultChatTransport({
      api: apiEndpoint,
      credentials: 'include',
      headers: async () => await buildHeaders(),
      prepareSendMessagesRequest: ({ messages: outgoingMessages }) => {
        const { data: requestData, selectedModel: model } = requestBodyRef.current;

        return {
          body: {
            ...requestData,
            ...(model && { model }),
            messages: outgoingMessages,
          },
        };
      },
    }),
    onError: () => {
      errorToast({ description: 'Failed to generate content' });
    },
  });

  const isBusy = status === 'streaming' || status === 'submitted';
  const derivedSuggestion = React.useMemo(() => deriveLatestSuggestion(messages), [messages]);

  const initialSuggestion = React.useMemo<LlmChatSuggestion | undefined>(
    () =>
      initialSuggestionData
        ? { data: initialSuggestionData, isInitial: true, isStreaming: false, kind: 'data', versionId: 'initial' }
        : undefined,
    [initialSuggestionData],
  );

  const latestSuggestion = React.useMemo(
    () => resolveSuggestionWithSeed({ derived: derivedSuggestion, seed: initialSuggestion }),
    [derivedSuggestion, initialSuggestion],
  );

  const { reset: resetResolutionLog, resolveLog } = useSuggestionResolutionLog(latestSuggestion);

  const resetState = React.useCallback(() => {
    setMessages([]);
    setInput('');
    setIsConfirmCloseOpen(false);
    setPendingComments([]);
    resetResolutionLog();
  }, [resetResolutionLog, setMessages]);

  // Prefill the chat input when the modal opens (prefilled, never auto-sent);
  // self-clean when it closes, even if a caller hides it without calling onClose.
  // See docs/iterations/AZ-323/AZ-323.DECISIONS.md (D14).
  React.useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setInput(initialInput ?? '');
    } else if (!isOpen && wasOpenRef.current) {
      resetState();
    }
    wasOpenRef.current = isOpen;
  }, [initialInput, isOpen, resetState]);

  const resetAndClose = () => {
    resetState();
    onClose();
  };

  const handleAccept = async (): Promise<void> => {
    if (!latestSuggestion || latestSuggestion.isInitial || latestSuggestion.isStreaming || isBusy || isAccepting) {
      return;
    }

    if (latestSuggestion.kind === 'text') {
      if (!onAccept) return;
      onAccept(latestSuggestion.content);
    } else {
      if (!onAcceptData || !latestSuggestion.data) return;

      setIsAccepting(true);
      try {
        const accepted = await onAcceptData(latestSuggestion.data, { logId: latestSuggestion.logId });
        if (accepted === false) return;
      } catch {
        // Persist failures already surface via the API error toast; keep the
        // modal open so the reviewed draft is not lost.
        return;
      } finally {
        setIsAccepting(false);
      }
    }

    resolveLog(latestSuggestion.logId, 'accept');
    resetAndClose();
  };

  // Closing with anything to lose (history, stream, draft, typed input, staged
  // comments) requires confirmation.
  // See docs/iterations/AZ-323/AZ-323.DECISIONS.md (D12).
  const requestClose = () => {
    const isPristine = messages.length === 0 && !isBusy && !input.trim() && pendingComments.length === 0;

    if (isPristine) {
      resetAndClose();
      return;
    }

    setIsConfirmCloseOpen(true);
  };

  const handleDiscardConfirm = () => {
    stop();
    resolveLog(latestSuggestion?.logId, 'reject');
    resetAndClose();
  };

  const handleGenerateDraft = () => {
    if (isBusy) {
      return;
    }

    sendMessage({
      role: 'user',
      parts: [{ type: 'text', text: generateDraftMessage || GENERATE_DRAFT_MESSAGE }],
    });
  };

  // Comments staged from the Suggestion panel become part of the next chat
  // message automatically (Codex-style); they never send on their own.
  const handleAddComment = (comment: { quote: string; text: string }) => {
    setPendingComments((current) => [...current, { id: generateUUID(), quote: comment.quote, text: comment.text }]);
    chatInputRef.current?.focus();
  };

  const handleRemoveComment = (id: string) => {
    setPendingComments((current) => current.filter((comment) => comment.id !== id));
  };

  const handleClearComments = () => {
    setPendingComments([]);
  };

  const emptyState = (
    <ConversationEmptyState className="text-center">
      <SparklesIcon className="size-6 text-gray-400" />
      <div className="max-w-md space-y-1">
        <h3 className="text-sm font-medium text-gray-800">Draft with the AI assistant</h3>
        <div className="text-sm text-gray-500">
          {description || 'Ask for a draft, then refine it in conversation until it is ready to accept.'}
        </div>
      </div>
      <Button
        onClick={handleGenerateDraft}
        size="sm"
        type="button"
        variant="primary"
      >
        <SparklesIcon size={14} />
        {generateDraftLabel || 'Generate draft'}
      </Button>
    </ConversationEmptyState>
  );

  return (
    <>
      <Modal
        centered
        contentClassname="flex min-h-0 overflow-hidden p-3"
        header={{
          className: 'text-gray-800',
          subtitle: information?.subtitle ? <span className="text-gray-700">{information?.subtitle}</span> : undefined,
          title: title || 'Generate Content',
        }}
        height="88vh"
        isOpen={isOpen}
        maxWidth="1500px"
        onClose={requestClose}
        width="95%"
      >
        <Group
          className="h-full min-h-0 w-full"
          defaultLayout={panelLayout.defaultLayout}
          onLayoutChanged={panelLayout.onLayoutChanged}
          orientation="horizontal"
        >
          <Panel
            defaultSize={45}
            id="chat"
            minSize={28}
          >
            <div className="flex h-full min-h-0 flex-col gap-2 pr-1">
              {information?.definition && (
                <Accordion
                  className="shrink-0 rounded-lg border"
                  collapsible
                  type="single"
                >
                  <AccordionItem
                    className="border-none"
                    value="definition"
                  >
                    <AccordionTrigger className="px-3 py-2 text-sm font-medium text-gray-700 hover:no-underline">
                      <div className="flex items-center gap-2">
                        <BookOpenIcon size={16} />
                        <span>Definition</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="max-h-48 overflow-y-auto whitespace-pre-wrap px-3 pb-3 text-xs text-gray-600">
                      {information.definition}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}

              <LlmChatMessages
                emptyState={emptyState}
                messages={messages}
                status={status}
              />

              <LlmChatInput
                input={input}
                modelFilter={modelFilter}
                onClearComments={handleClearComments}
                onRemoveComment={handleRemoveComment}
                pendingComments={pendingComments}
                sendMessage={sendMessage}
                setInput={setInput}
                status={status}
                stop={stop}
                textareaRef={chatInputRef}
              />
            </div>
          </Panel>

          <Separator className="group flex w-3 items-center justify-center outline-none">
            <div className="flex h-16 w-1.5 items-center justify-center rounded-full bg-transparent transition-colors group-hover:bg-gray-200">
              <GripVertical className="size-4 text-gray-400" />
            </div>
          </Separator>

          <Panel
            defaultSize={55}
            id="suggestion"
            minSize={30}
          >
            <div className="h-full min-h-0 pl-1">
              <SuggestionArtifactPanel
                isBusy={isBusy || isAccepting}
                onAccept={handleAccept}
                onAddComment={handleAddComment}
                renderData={renderDataArtifact}
                suggestion={latestSuggestion}
              />
            </div>
          </Panel>
        </Group>
      </Modal>

      <ConfirmationModal
        confirmLabel="Discard"
        description="Closing will discard this conversation and the current draft. This cannot be undone."
        isOpen={isConfirmCloseOpen}
        onConfirm={handleDiscardConfirm}
        setIsOpen={setIsConfirmCloseOpen}
        title="Discard this conversation?"
        variant="destructive"
      />
    </>
  );
};
